'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'

import { ButtonV2, Popover, PopoverContent, PopoverTrigger } from '@fsg/gui-bits'
import { ChevronDown, ChevronUp, MoreHorizontal } from '@fsg/icons'

type ListProps = {
  data: any[]
  renderListItem?: (item: any, index?: any) => React.ReactNode
  header?: string
  moreActions?: React.ReactNode
  isLoading?: boolean
  overlay?: React.ReactNode
  className?: string
  allowMinimize?: boolean
  minDisplay?: number
  showAll?: boolean
}

type ListContextType = {
  data: any[]
  visibleList: any[]
  setVisibleList: React.Dispatch<React.SetStateAction<any[]>>
  showMoreItems: () => void
  showLessItems: () => void
  moreActions: React.ReactNode
  sliceSize: number
  hasData: boolean
  allowMinimize?: boolean
}

const ListContext = createContext<ListContextType | undefined>(undefined)

export function useListContext() {
  const context = useContext(ListContext)
  if (context === undefined) throw new Error('Cannot call useListContext outside of a List component.')
  return context
}

export function List(props: React.PropsWithChildren<ListProps>) {
  const {
    header,
    moreActions,
    overlay,
    data,
    renderListItem,
    className = null,
    allowMinimize = false,
    minDisplay: sliceSize = 8,
    showAll = false,
  } = props

  const [visibleList, setVisibleList] = useState(data.slice(0, sliceSize))

  useEffect(() => {
    if (showAll) {
      setVisibleList(data.slice(0, data.length))
    } else {
      setVisibleList(data.slice(0, sliceSize))
    }
  }, [data, sliceSize, showAll])

  const showMoreItems = () => {
    if (visibleList.length === data.length) return
    setVisibleList((prev) => [...prev, ...data.slice(prev.length, prev.length + sliceSize)])
  }

  const showLessItems = () => {
    if (visibleList.length === data.length) {
      setVisibleList(data.slice(0, sliceSize))
    }
  }

  const hasData = data && data.length > 0

  const context = {
    data,
    visibleList,
    setVisibleList,
    showMoreItems,
    showLessItems,
    moreActions,
    sliceSize,
    hasData,
    allowMinimize,
  }

  const listContent = hasData ? (
    <>
      <ListItems>{visibleList.map(renderListItem)}</ListItems>
      <ShowMoreButton />
    </>
  ) : (
    overlay
  )

  return (
    <ListContext.Provider value={context}>
      <div className={className ?? 'rounded-lg border border-gray-400 p-[16px]'}>
        {(moreActions || header) && (
          <div className="flex items-center justify-between">
            {header && <h4 className="my-xs text-md font-bold">{header}</h4>}
            {moreActions && <MoreActions>{moreActions}</MoreActions>}
          </div>
        )}
        {listContent}
      </div>
    </ListContext.Provider>
  )
}

function ListItems(props: React.PropsWithChildren) {
  return <ul>{props.children}</ul>
}

interface ListItemProps extends React.PropsWithChildren {
  className?: string
}
function ListItem(props: ListItemProps) {
  const { children, className } = props

  return <li className={className}>{children}</li>
}

List.ListItem = ListItem

function MoreActions(props: React.PropsWithChildren) {
  return props.children ? (
    <Popover>
      <PopoverTrigger>
        <ButtonV2 asChild variant="ghost" className="px-xs pb-xs pt-xs" size="lg">
          <MoreHorizontal />
        </ButtonV2>
      </PopoverTrigger>
      <PopoverContent align="end">
        <div className="flex flex-col  gap-3xs">{props.children}</div>
      </PopoverContent>
    </Popover>
  ) : null
}

function ShowMoreButton() {
  const { data, showMoreItems, showLessItems, visibleList, sliceSize, allowMinimize } = useListContext()

  const handleItemsExpansion = () => {
    if (allowMinimize) {
      if (data.length === visibleList.length) {
        showLessItems()
      } else {
        showMoreItems()
      }
    } else {
      showMoreItems()
    }
  }

  return data && data.length > sliceSize ? (
    // ButtonV2 styles have specificity issues with some properties
    <button
      type="button"
      className="mt-sm flex w-full justify-center rounded-full border-none bg-gray-200 p-[6px] text-sm text-gray-900"
      onClick={handleItemsExpansion}
      disabled={data.length === visibleList.length && !allowMinimize}
    >
      {data.length === visibleList.length ? allowMinimize ? <ChevronUp /> : 'No more items' : <ChevronDown />}
    </button>
  ) : null
}
List.ShowMoreButton = ShowMoreButton
